import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryDialog = ({ open, onOpenChange, pkg }) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    travelers: "",
    preferred_date: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", travelers: "", preferred_date: "", message: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        travelers: form.travelers ? Number(form.travelers) : null,
        preferred_date: form.preferred_date || null,
        message: form.message || null,
        package_id: pkg?.id || null,
        package_name: pkg?.name || null,
      });
      setDone(true);
      toast.success("Inquiry sent! Our travel team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) setTimeout(reset, 200);
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="inquiry-dialog"
        className="max-w-lg rounded-3xl border-slate-100 bg-white p-0 overflow-hidden"
      >
        {done ? (
          <div className="flex flex-col items-center px-8 py-14 text-center">
            <CheckCircle2 className="h-16 w-16 text-secondary" />
            <h3 className="mt-5 font-display text-2xl font-bold text-navy">
              You're on the list!
            </h3>
            <p className="mt-2 max-w-xs text-slate-500">
              Thanks, {form.name.split(" ")[0] || "traveler"} — a Navigator specialist will
              email you a curated, no-pressure plan within 24 hours.
            </p>
            <Button
              data-testid="inquiry-close-button"
              onClick={() => handleOpenChange(false)}
              className="mt-7 rounded-full bg-navy px-8 font-semibold text-white hover:bg-navy/90"
            >
              Done
            </Button>
          </div>
        ) : (
          <div>
            <div className="bg-navy px-8 py-7">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-white">
                  Inquire About This Escape
                </DialogTitle>
                <DialogDescription className="text-slate-300">
                  {pkg
                    ? `Get a personalized, no-pressure quote for ${pkg.name}.`
                    : "Tell us your dream trip — we'll handle the rest, zero stress."}
                </DialogDescription>
              </DialogHeader>
            </div>
            <form onSubmit={submit} className="space-y-4 px-8 py-7">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" required>
                  <Input
                    data-testid="inquiry-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jordan Rivera"
                    className="rounded-xl"
                  />
                </Field>
                <Field label="Email" required>
                  <Input
                    data-testid="inquiry-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="rounded-xl"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    data-testid="inquiry-phone-input"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="rounded-xl"
                  />
                </Field>
                <Field label="Travelers">
                  <Input
                    data-testid="inquiry-travelers-input"
                    type="number"
                    min="1"
                    value={form.travelers}
                    onChange={update("travelers")}
                    placeholder="2"
                    className="rounded-xl"
                  />
                </Field>
              </div>
              <Field label="Preferred travel date">
                <Input
                  data-testid="inquiry-date-input"
                  type="date"
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                  className="rounded-xl"
                />
              </Field>
              <Field label="Anything we should know?">
                <Textarea
                  data-testid="inquiry-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Occasion, must-haves, questions..."
                  className="min-h-[90px] rounded-xl"
                />
              </Field>
              <Button
                type="submit"
                data-testid="inquiry-submit-button"
                disabled={submitting}
                className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  "Send My Inquiry"
                )}
              </Button>
              <p className="text-center text-xs text-slate-400">
                No payment required. No spam. Just a transparent, human reply.
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-navy">
      {label}
      {required && <span className="text-primary"> *</span>}
    </Label>
    {children}
  </div>
);
